import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  FileText, Loader2, ChevronRight, Send, X,
  Mail, User, MessageSquare, CheckCircle, Copy
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TEMPLATES = [
  { key: "purchase_agreement", label: "Purchase Agreement",   desc: "Full PSA with all clauses",      color: "text-[#D4AF37]",  border: "border-[#D4AF37]/20", bg: "bg-[#D4AF37]/[0.04]",  sendLabel: "Send to Seller / Buyer"  },
  { key: "assignment",         label: "Assignment Agreement", desc: "Assign your contract + fee",     color: "text-[#0A84FF]",  border: "border-[#0A84FF]/20", bg: "bg-[#0A84FF]/[0.04]",  sendLabel: "Send to End Buyer"       },
  { key: "proof_of_funds",     label: "Proof of Funds",       desc: "POF letter for offers",          color: "text-[#00FF88]",  border: "border-[#00FF88]/20", bg: "bg-[#00FF88]/[0.04]",  sendLabel: "Send to Listing Agent"   },
  { key: "seller_finance",     label: "Seller Finance",       desc: "Seller financing addendum",      color: "text-[#BF5AF2]",  border: "border-[#BF5AF2]/20", bg: "bg-[#BF5AF2]/[0.04]",  sendLabel: "Send to Seller"          },
  { key: "scope_of_work",      label: "Scope of Work",        desc: "Rehab scope for contractors",    color: "text-[#FF9F0A]",  border: "border-[#FF9F0A]/20", bg: "bg-[#FF9F0A]/[0.04]",  sendLabel: "Send to Contractor"      },
];

function SendToPartnerModal({ template, deal, onClose }) {
  const [partnerEmail, setPartnerEmail] = useState("");
  const [partnerName, setPartnerName]   = useState("");
  const [note, setNote]                 = useState("");
  const [sending, setSending]           = useState(false);
  const [sent, setSent]                 = useState(false);
  const [fallback, setFallback]         = useState(false);

  const handleSend = async () => {
    if (!partnerEmail.trim()) { toast.error("Partner email is required"); return; }
    setSending(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/network/send-to-partner`, {
        partner_email: partnerEmail,
        partner_name: partnerName || "Partner",
        deal_id: deal?.deal_id,
        document_type: template.key,
        note,
      }, { withCredentials: true });

      if (res.data.fallback) {
        // Email not sent — copy fallback content to clipboard
        const fallbackText = `Hi ${partnerName || "there"},\n\nI'd like to share a ${template.label} with you for ${deal?.property_address || "a deal"}.\n\n${note ? `Note: ${note}\n\n` : ""}— Sent via DealForge`;
        navigator.clipboard.writeText(fallbackText).catch(() => {});
        setFallback(true);
      } else {
        setSent(true);
        toast.success(`${template.label} sent to ${partnerName || partnerEmail}!`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        className="relative bg-[#111] border border-white/[0.1] rounded-2xl w-full max-w-sm shadow-2xl shadow-black"
        onClick={e => e.stopPropagation()}
        data-testid="send-to-partner-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <Send className={`w-4 h-4 ${template.color}`} />
            <p className={`text-sm font-bold ${template.color}`}>Send to Partner</p>
          </div>
          <button onClick={onClose} className="text-white/30 hover:text-white/70 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {sent ? (
          <div className="p-8 text-center">
            <div className="w-12 h-12 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mx-auto mb-3">
              <CheckCircle className="w-6 h-6 text-emerald-400" />
            </div>
            <p className="text-white font-bold mb-1">Email sent!</p>
            <p className="text-white/40 text-xs">{template.label} delivered to {partnerName || partnerEmail}</p>
            <button onClick={onClose} className="mt-5 px-5 py-2 text-xs font-bold text-black bg-[#D4AF37] rounded-lg hover:bg-[#C4A030] transition-all">Done</button>
          </div>
        ) : fallback ? (
          <div className="p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center mx-auto mb-3">
              <Copy className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <p className="text-white font-bold mb-1">Copied to clipboard!</p>
            <p className="text-white/40 text-xs leading-relaxed">
              Email delivery unavailable — message text has been copied.<br />
              Paste it into your email to send manually.
            </p>
            <p className="text-white/20 text-[10px] mt-2">Add your SendGrid key in Settings to enable direct sending.</p>
            <button onClick={onClose} className="mt-5 px-5 py-2 text-xs font-bold text-black bg-[#D4AF37] rounded-lg hover:bg-[#C4A030] transition-all">Done</button>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            {/* Doc preview */}
            <div className={`flex items-center gap-2.5 p-3 rounded-xl border ${template.border} ${template.bg}`}>
              <FileText className={`w-4 h-4 ${template.color} flex-shrink-0`} />
              <div>
                <p className={`text-xs font-bold ${template.color}`}>{template.label}</p>
                {deal?.property_address && <p className="text-white/30 text-[10px]">{deal.property_address}</p>}
              </div>
            </div>

            {/* Partner fields */}
            <div>
              <label className="text-[10px] text-white/30 uppercase tracking-wider block mb-1.5">Partner Email *</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/25" />
                <input
                  type="email"
                  value={partnerEmail}
                  onChange={e => setPartnerEmail(e.target.value)}
                  placeholder="partner@example.com"
                  data-testid="send-partner-email"
                  className="w-full bg-[#0A0A0A] border border-white/[0.1] rounded-lg pl-9 pr-4 py-2.5 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-[#D4AF37]/40"
                />
              </div>
            </div>

            <div>
              <label className="text-[10px] text-white/30 uppercase tracking-wider block mb-1.5">Partner Name</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/25" />
                <input
                  type="text"
                  value={partnerName}
                  onChange={e => setPartnerName(e.target.value)}
                  placeholder="First name or full name"
                  data-testid="send-partner-name"
                  className="w-full bg-[#0A0A0A] border border-white/[0.1] rounded-lg pl-9 pr-4 py-2.5 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-[#D4AF37]/40"
                />
              </div>
            </div>

            <div>
              <label className="text-[10px] text-white/30 uppercase tracking-wider block mb-1.5">Note (optional)</label>
              <div className="relative">
                <MessageSquare className="absolute left-3 top-3 w-3.5 h-3.5 text-white/25" />
                <textarea
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  placeholder={template.sendLabel + "…"}
                  rows={2}
                  data-testid="send-partner-note"
                  className="w-full bg-[#0A0A0A] border border-white/[0.1] rounded-lg pl-9 pr-4 py-2.5 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-[#D4AF37]/40 resize-none"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-1">
              <button onClick={onClose}
                className="flex-1 py-2.5 text-xs text-white/40 border border-white/[0.1] rounded-xl hover:text-white transition-all">
                Cancel
              </button>
              <button onClick={handleSend} disabled={sending || !partnerEmail.trim()}
                data-testid="send-partner-submit"
                className="flex-1 py-2.5 text-xs font-bold text-black bg-gradient-to-r from-[#D4AF37] to-[#C4A030] rounded-xl disabled:opacity-40 hover:from-[#C4A030] hover:to-[#B49020] transition-all flex items-center justify-center gap-1.5">
                {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                {sending ? "Sending…" : "Send"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function SmartTemplatesPanel({ deal }) {
  const [generating, setGenerating] = useState(null);
  const [sendModal, setSendModal]   = useState(null); // template key
  const navigate = useNavigate();

  const handleGenerate = async (templateKey) => {
    setGenerating(templateKey);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/contracts/generate-from-deal`,
        { deal_id: deal.deal_id, template_type: templateKey },
        { withCredentials: true }
      );
      toast.success(`${res.data.contract_name} created — opening editor`);
      navigate(`/contracts?open=${res.data.contract_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Template generation failed");
    } finally {
      setGenerating(null);
    }
  };

  const activeTemplate = TEMPLATES.find(t => t.key === sendModal);

  return (
    <>
      <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-4" data-testid="smart-templates-panel">
        <h3 className="font-heading text-sm font-bold text-white uppercase flex items-center gap-2 mb-3">
          <FileText className="w-4 h-4 text-[#D4AF37]" />
          Smart Templates
        </h3>
        <p className="text-white/30 text-[11px] mb-3 leading-relaxed">Auto-filled from this deal's data</p>
        <div className="space-y-2">
          {TEMPLATES.map(t => (
            <div key={t.key} className={`flex items-center gap-1.5 border rounded-lg overflow-hidden ${t.border} ${t.bg}`}>
              {/* Generate button */}
              <button
                onClick={() => handleGenerate(t.key)}
                disabled={!!generating}
                data-testid={`template-btn-${t.key}`}
                className="flex-1 flex items-center justify-between p-2.5 transition-all hover:opacity-90 disabled:opacity-50"
              >
                <div className="text-left">
                  <p className={`text-xs font-semibold ${t.color}`}>{t.label}</p>
                  <p className="text-white/30 text-[10px]">{t.desc}</p>
                </div>
                {generating === t.key ? (
                  <Loader2 className={`w-3.5 h-3.5 animate-spin flex-shrink-0 ${t.color}`} />
                ) : (
                  <ChevronRight className={`w-3.5 h-3.5 flex-shrink-0 ${t.color}`} />
                )}
              </button>
              {/* Send to Partner button */}
              <button
                onClick={() => setSendModal(t.key)}
                disabled={!!generating}
                data-testid={`send-template-btn-${t.key}`}
                title={t.sendLabel}
                className={`flex-shrink-0 flex items-center justify-center w-9 border-l border-white/[0.06] h-full py-2.5 ${t.color} hover:opacity-80 transition-all disabled:opacity-30`}
              >
                <Send className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {sendModal && activeTemplate && (
        <SendToPartnerModal
          template={activeTemplate}
          deal={deal}
          onClose={() => setSendModal(null)}
        />
      )}
    </>
  );
}
